/**
 * Brand icons for OAuth providers and Latitude brand
 *
 * Custom SVG icons for social login providers (Google, GitHub) and Latitude logo
 * These have specific brand colors and designs not available in Lucide
 */

import { cn } from "../../utils/cn.ts"

export interface BrandIconProps {
  className?: string
}

/**
 * Latitude logo icon
 */
export const LatitudeLogo = ({ className }: BrandIconProps) => (
  <svg className={cn("h-8 w-8", className)} viewBox="0 0 20 20" fill="none" aria-label="Latitude logo">
    <title>Latitude</title>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.92908e-05 12.9825C-0.000146101 12.9876 0.000264683 12.9929 5.92915e-05 12.998L2.28025 12.998C2.53495 8.96957 5.90153 5.77716 10.0001 5.77716C14.0936 5.77716 17.4501 8.96879 17.712 12.998L20 12.998C19.74 7.70424 15.3652 3.49923 10.0002 3.49923C4.64031 3.49923 0.267973 7.69573 0.000322466 12.9823L5.92908e-05 12.9825Z"
      fill="#0080FF"
    />
    <path
      d="M19.9998 13.9941C19.9578 14.864 19.8043 15.7039 19.5547 16.5007L0.445068 16.5007C0.19543 15.7039 0.0420302 14.864 0 13.9941L19.9998 13.9941Z"
      fill="#030712"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.28458 12.998L6.00826 12.998C6.25515 11.0171 7.95449 9.48451 10.0005 9.48451C12.0416 9.48451 13.7292 11.0239 13.9849 12.998L16.7164 12.998C16.459 9.50688 13.5566 6.77195 10.0004 6.77195C6.44155 6.77195 3.5384 9.51261 3.28442 12.998L3.28458 12.998Z"
      fill="#E63948"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.02004 12.998L12.973 12.998C12.729 11.5654 11.4995 10.4718 10.0002 10.4718C8.49606 10.4718 7.25845 11.5639 7.01978 12.998L7.02004 12.998Z"
      fill="#FEC61A"
    />
  </svg>
)

export const LatitudeWordmark = ({ className }: BrandIconProps) => (
  <svg className={cn("h-5 w-auto", className)} viewBox="0 0 458 86" fill="none" aria-label="Latitude">
    <title>Latitude</title>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.000237163 57.9327C-0.000584403 57.9533 0.00105873 57.9746 0.000237166 57.9951L9.12098 57.9951C10.1398 41.8812 23.6061 29.1116 40.0002 29.1116C56.3744 29.1116 69.8005 41.8781 70.8479 57.9951L80 57.9951C78.9598 36.8199 61.4609 19.9999 40.0006 19.9999C18.5612 19.9999 1.07189 36.7858 0.00128986 57.9321L0.000237163 57.9327Z"
      fill="#0080FF"
    />
    <path
      className="fill-[#071324] dark:fill-white"
      d="M79.999 61.9736C79.8311 65.4532 79.2172 68.8128 78.2188 72L1.78027 72C0.781719 68.8129 0.168121 65.4531 0 61.9736L79.999 61.9736Z"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.1356 57.9941L24.0304 57.9941C25.0179 50.0705 31.8153 43.94 39.9994 43.94C48.1637 43.94 54.9142 50.0976 55.9371 57.9941L66.8629 57.9941C65.8334 44.0295 54.2239 33.0898 39.999 33.0898C25.7635 33.0898 14.1509 44.0524 13.135 57.9941L13.1356 57.9941Z"
      fill="#E63948"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M28.078 57.9941L51.89 57.9941C50.9139 52.2634 45.9957 47.8891 39.9987 47.8891C33.9821 47.8891 29.0316 52.2576 28.0769 57.9941L28.078 57.9941Z"
      fill="#FEC61A"
    />
    <path className="fill-[#071324] dark:fill-white" d="M147.471 71.7283H104V8.47532H117.214V59.952H147.471V71.7283Z" />
    <path
      className="fill-[#071324] dark:fill-white"
      d="M153.353 59.7736C153.353 51.9227 159.577 47.5512 167.428 46.4806L179.014 44.8748C181.695 44.5179 182.557 43.2689 182.557 41.7523C182.557 38.6298 179.971 36.0426 174.609 36.0426C169.056 36.0426 165.992 39.3435 165.609 43.1797L154.31 40.9493C155.076 34.0798 161.875 26.4966 174.514 26.4966C189.451 26.4966 195.004 34.3475 195.004 43.1797V64.7696C195.004 67.0892 195.292 70.2117 195.579 71.7283H183.897C183.61 70.5685 183.418 68.1597 183.418 66.4647C181.025 69.944 176.524 72.9773 169.535 72.9773C159.481 72.9773 153.353 66.6431 153.353 59.7736ZM172.216 64.1451C177.578 64.1451 182.557 61.7363 182.557 53.9746V52.0119L171.928 53.5286C168.673 53.9746 166.088 55.6697 166.088 59.0599C166.088 61.6471 168.098 64.1451 172.216 64.1451Z"
    />
    <path
      className="fill-[#071324] dark:fill-white"
      d="M222.306 14.7203V27.8348H231.785V38.3621H222.306V56.7403C222.306 60.5765 224.221 61.8255 227.86 61.8255C229.392 61.8255 231.115 61.6471 231.785 61.4687V71.2822C230.636 71.7283 228.338 72.3528 224.604 72.3528C215.412 72.3528 209.667 67.2676 209.667 58.7922V38.3621H201.145V27.8348H203.539C208.518 27.8348 210.816 24.8015 210.816 20.8761V14.7203H222.306Z"
    />
    <path
      className="fill-[#071324] dark:fill-white"
      d="M254.38 71.7283H241.645V27.8348H254.38V71.7283ZM240.113 13.4713C240.113 9.36746 243.656 6.06653 247.965 6.06653C252.369 6.06653 255.816 9.36746 255.816 13.4713C255.816 17.3968 252.369 20.6977 247.965 20.6977C243.656 20.6977 240.113 17.3968 240.113 13.4713Z"
    />
    <path
      className="fill-[#071324] dark:fill-white"
      d="M282.76 14.7203V27.8348H292.239V38.3621H282.76V56.7403C282.76 60.5765 284.675 61.8255 288.313 61.8255C289.845 61.8255 291.569 61.6471 292.239 61.4687V71.2822C291.09 71.7283 288.792 72.3528 285.058 72.3528C275.866 72.3528 270.121 67.2676 270.121 58.7922V38.3621H261.599V27.8348H263.993C268.972 27.8348 271.27 24.8015 271.27 20.8761V14.7203H282.76Z"
    />
    <path
      className="fill-[#071324] dark:fill-white"
      d="M333.211 71.7283C333.02 70.8362 332.828 68.7842 332.828 66.9999C330.243 71.1038 324.785 72.8881 319.806 72.8881C308.412 72.8881 301.901 65.1264 301.901 55.5805V27.8348H314.636V53.1717C314.636 58.0785 317.317 62.0039 323.253 62.0039C328.903 62.0039 332.158 58.4354 332.158 53.3501V27.8348H344.893V63.7882C344.893 67.1784 345.18 70.2117 345.372 71.7283H333.211Z"
    />
    <path
      className="fill-[#071324] dark:fill-white"
      d="M402.246 7.1371V63.7882C402.246 66.7323 402.438 69.944 402.629 71.7283H390.469C390.277 70.8362 389.99 68.695 389.99 66.6431C387.884 70.1225 383.096 72.7989 376.776 72.7989C363.371 72.7989 353.796 62.9853 353.796 49.6924C353.796 36.8455 363.084 26.7643 376.394 26.7643C384.532 26.7643 388.362 29.8868 389.703 32.2063V7.1371H402.246ZM366.627 49.6924C366.627 57.5432 371.606 62.1824 378.309 62.1824C384.82 62.1824 389.894 57.5432 389.894 49.6031C389.894 41.7523 384.82 37.3808 378.309 37.3808C371.797 37.3808 366.627 41.8415 366.627 49.6924Z"
    />
    <path
      className="fill-[#071324] dark:fill-white"
      d="M424.392 44.7856H445.457C445.265 40.4141 442.201 36.1318 434.924 36.1318C428.317 36.1318 424.679 40.7709 424.392 44.7856ZM446.701 56.205L457.33 59.1491C454.936 66.7323 447.467 73.0665 435.882 73.0665C422.955 73.0665 411.561 64.4127 411.561 49.6031C411.561 35.5965 422.668 26.4966 434.733 26.4966C449.287 26.4966 458 35.1504 458 49.2463C458 50.9414 457.809 52.7256 457.809 52.9041H424.104C424.392 58.703 429.658 62.8961 435.977 62.8961C441.914 62.8961 445.169 60.1304 446.701 56.205Z"
    />
  </svg>
)

/**
 * Google logo icon
 */
export const GoogleIcon = ({ className }: BrandIconProps) => (
  <svg className={cn("h-4 w-4", className)} viewBox="0 0 24 24" aria-label="Google logo">
    <title>Google</title>
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
)

/**
 * GitHub logo icon
 */
export const GitHubIcon = ({ className }: BrandIconProps) => (
  <svg className={cn("h-4 w-4", className)} fill="currentColor" viewBox="0 0 24 24" aria-label="GitHub logo">
    <title>GitHub</title>
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
)
